import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, FileUp, Loader2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { haccpApi, erroreHaccp } from '../haccpApi';
import { BozzaForm } from './BozzaForm';

// Più file insieme: l'AI li legge uno alla volta, poi si controllano e si salvano
export const CaricaDocumenti = ({ preset, catalogo, persone, onClose, onSalvato }) => {
  const input = useRef(null);
  const [bozze, setBozze] = useState([]);
  useEffect(() => { if (preset) setBozze([]); }, [preset]);
  const aggiorna = (key, dati) => setBozze((bs) => bs.map((b) => (b.key === key ? { ...b, ...dati } : b)));

  const leggi = async (files) => {
    const nuove = Array.from(files).map((f) => ({ key: `${f.name}-${Math.random()}`, file: f, stato: 'lettura' }));
    setBozze((bs) => [...bs, ...nuove]);
    for (const b of nuove) {
      const fd = new FormData();
      fd.append('file', b.file);
      if (preset?.tipo) fd.append('tipo', preset.tipo);
      try {
        const r = await haccpApi.post('/documenti/leggi', fd);
        const p = r.proposta;
        aggiorna(b.key, { stato: 'pronta', bozza_id: r.bozza_id, letto: r.letto,
          form: { ...p, persona_id: preset?.persona_id || p.persona_id || '', persona_nome: p.persona_id ? '' : (p.intestatario || '') } });
      } catch (e) { aggiorna(b.key, { stato: 'errore', errore: erroreHaccp(e, 'File non caricato') }); }
    }
    if (input.current) input.current.value = '';
  };
  const salva = async (b) => {
    aggiorna(b.key, { salvando: true });
    try {
      const f = b.form;
      await haccpApi.post('/documenti', { bozza_id: b.bozza_id, tipo: f.tipo, titolo: f.titolo, persona_id: f.persona_id || null,
        persona_nome: f.persona_id ? null : f.persona_nome, data_documento: f.data_documento || null, scadenza: f.scadenza || null, ente: f.ente || null });
      aggiorna(b.key, { stato: 'salvata', salvando: false });
      onSalvato();
    } catch (e) { aggiorna(b.key, { salvando: false }); toast.error(erroreHaccp(e, 'Documento non salvato')); }
  };
  const pronte = bozze.filter((b) => b.stato === 'pronta');

  return (
    <Dialog open={!!preset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[96vw] max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="documenti-carica-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6">{preset?.titolo || 'Carica documenti'}</DialogTitle>
          <DialogDescription>Foto o PDF, anche più file insieme: l'AI legge tipo, intestatario e scadenza. Controlla e salva.</DialogDescription>
        </DialogHeader>
        <button type="button" onClick={() => input.current?.click()} data-testid="documenti-scegli-file"
          className="w-full rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50 p-4 flex items-center gap-3 text-left">
          <FileUp className="h-7 w-7 text-sky-700" />
          <span><span className="block font-semibold text-sky-900">Scegli foto o PDF</span><span className="text-xs text-sky-800">Puoi selezionarne più di uno</span></span>
          <input ref={input} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.length && leggi(e.target.files)} data-testid="documenti-file-input" />
        </button>
        {bozze.map((b, i) => (
          <div key={b.key} className="rounded-2xl border border-gray-200 bg-white p-3 space-y-3" data-testid={`documenti-bozza-${i}`}>
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2 break-all">
              {b.stato === 'lettura' && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
              {b.stato === 'salvata' && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
              {b.stato === 'errore' && <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
              {b.file.name}
            </p>
            {b.stato === 'lettura' && <p className="text-xs text-gray-500">Leggo il documento...</p>}
            {b.stato === 'errore' && <p className="text-xs text-red-700">{b.errore}</p>}
            {b.stato === 'salvata' && <p className="text-xs text-emerald-700" data-testid={`documenti-bozza-${i}-salvata`}>Salvato</p>}
            {b.stato === 'pronta' && (
              <>
                <BozzaForm b={b} catalogo={catalogo} persone={persone} onCambia={(form) => aggiorna(b.key, { form })} testid={`documenti-bozza-${i}`} />
                <button onClick={() => salva(b)} disabled={b.salvando} data-testid={`documenti-bozza-${i}-salva`}
                  className="w-full h-11 rounded-full bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                  {b.salvando && <Loader2 className="h-4 w-4 animate-spin" />}Salva
                </button>
              </>
            )}
          </div>
        ))}
        {pronte.length > 1 && (
          <button onClick={() => pronte.forEach(salva)} data-testid="documenti-salva-tutti"
            className="w-full h-12 rounded-full bg-sky-700 text-white font-semibold">Salva tutti ({pronte.length})</button>
        )}
      </DialogContent>
    </Dialog>
  );
};
